import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stack,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  FormHelperText,
  Autocomplete,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import AppointmentService from '../../services/AppointmentService.js';
import PatientService from '../../services/PatientService.js';
import PhysicianService from '../../services/PhysicianService.js';
import HolidayService from '../../services/HolidayService.js';
import { apiClient } from '../../services/api.js';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow
});
const AppointmentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = Boolean(id);
  
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isHoliday, setIsHoliday] = useState(false);
  const [holidayName, setHolidayName] = useState('');
  const [skippedHolidays, setSkippedHolidays] = useState([]);
  
  // Patient list for dropdown
  const [patients, setPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(true);
  
  // Physician list for dropdown
  const [physicians, setPhysicians] = useState([]);
  const [loadingPhysicians, setLoadingPhysicians] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    patientId: '',
    patientName: '',
    date: location.state?.selectedDate || null,
    time: location.state?.selectedDate || null,
    duration: 30,
    type: 'Check-up',
    physicianId: '', // Changed to physicianId to store the ID instead of name
    physician: '', // Keep physician name for display purposes
    status: 'Scheduled',
    notes: '',
    isHouseCall: false,
    serviceAddress: '',
    serviceCity: '',
    serviceState: 'Penang',
    servicePostalCode: '',
    isRecurring: false,
    recurrenceType: 'Weekly',
    interval: 1,
    daysOfWeek: [],
    endDate: null,
    occurrences: 4,
    numberOfPersons: 1,
    isNewPatient: false,
    newPatient: {
      nickname: '',
      firstName: '',
      lastName: '',
      dateOfBirth: null,
      gender: '',
      phoneNumber: '',
      email: '',
      address: '',
      city: '',
      state: '',
      postalCode: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      insuranceProvider: '',
      insurancePolicyNumber: '',
      medicalRecordNumber: '',
      officialDocumentNumber: ''
    }
  });
  const [addressQuery, setAddressQuery] = useState('');
  const [addressOptions, setAddressOptions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [mapLat, setMapLat] = useState(null);
  const [mapLon, setMapLon] = useState(null);
  const mapContainerRef = React.useRef(null);
  const [leafletMap, setLeafletMap] = useState(null);
  const [leafletMarker, setLeafletMarker] = useState(null);
  const [usePatientAddress, setUsePatientAddress] = useState(false);
  const [addressSelected, setAddressSelected] = useState(null);
  
  useEffect(() => {
    // Fetch patients for dropdown
    const fetchPatients = async () => {
      try {
        setLoadingPatients(true);
        
        // Fetch active patients from the API
        const response = await PatientService.getActivePatients();
        const patientsData = response.data || [];
        setPatients(patientsData);

        if (!isEditMode) {
          const params = new URLSearchParams(location.search);
          const prefilledPatientId = params.get('patientId');
          if (prefilledPatientId) {
            const idNum = parseInt(prefilledPatientId);
            const selectedPatient = patientsData.find(p => p.patientId === idNum);
            if (selectedPatient) {
              setFormData(prev => ({
                ...prev,
                patientId: idNum,
                patientName: `${selectedPatient.firstName} ${selectedPatient.lastName}`
              }));
            } else {
              try {
                const singleRes = await PatientService.getPatientById(idNum);
                const p = singleRes.data;
                if (p) {
                  setPatients(prev => [...prev, p]);
                  setFormData(prev => ({
                    ...prev,
                    patientId: idNum,
                    patientName: `${p.firstName} ${p.lastName}`
                  }));
                } else {
                  setFormData(prev => ({
                    ...prev,
                    patientId: idNum
                  }));
                }
              } catch (e) {
                setFormData(prev => ({
                  ...prev,
                  patientId: idNum
                }));
              }
            }
          }
        }
        setLoadingPatients(false);
      } catch (error) {
        console.error('Error fetching patients:', error);
        setLoadingPatients(false);
      }
    };
    
    // Fetch physicians for dropdown
    const fetchPhysicians = async () => {
      try {
        setLoadingPhysicians(true);
        
        // Fetch active physicians from the API
        const response = await PhysicianService.getActivePhysicians();
        // The new API returns data directly, not wrapped in response.data
        const physiciansData = Array.isArray(response) ? response : response.data || [];
        
        // Map the user data to physician format expected by the form
        const mappedPhysicians = physiciansData.map(physician => ({
          physicianId: String(physician.physicianId),
          firstName: physician.firstName,
          lastName: physician.lastName,
          email: physician.email
        }));
        
        setPhysicians(mappedPhysicians);
        setLoadingPhysicians(false);
      } catch (error) {
        console.error('Error fetching physicians:', error);
        setLoadingPhysicians(false);
        // Fallback to default physicians if API fails
        setPhysicians([
          { physicianId: '1', firstName: 'Dr.', lastName: 'Johnson' },
          { physicianId: '2', firstName: 'Dr.', lastName: 'Williams' },
          { physicianId: '3', firstName: 'Dr.', lastName: 'Davis' },
          { physicianId: '4', firstName: 'Dr.', lastName: 'Miller' },
          { physicianId: '5', firstName: 'Dr.', lastName: 'Wilson' }
        ]);
      }
    };
    
    fetchPatients();
    fetchPhysicians();
    
    // If in edit mode, fetch appointment data
    if (isEditMode) {
      const fetchAppointmentData = async () => {
        try {
          setLoading(true);
          setError(null);
          
          // Fetch appointment data from the API
          const response = await AppointmentService.getAppointmentById(parseInt(id));
          const appointment = response.data;
          
          // Convert string date to Date object
          // Backend returns 'appointmentDate' but frontend expects 'date'
          const dateValue = appointment.appointmentDate || appointment.date;
          const appointmentDate = new Date(dateValue);
          
          // Create time Date object
          const timeDate = new Date(dateValue);
          let hours = 0, minutes = 0;
          // Backend returns 'startTime' but frontend expects 'time'
          const timeValue = appointment.startTime || appointment.time;
          if (timeValue && typeof timeValue === 'string') {
            const timeParts = timeValue.split(':');
            hours = parseInt(timeParts[0]) || 0;
            minutes = parseInt(timeParts[1]) || 0;
          }
          timeDate.setHours(hours, minutes, 0);
          
          // Get patient name
          const patientResponse = await PatientService.getPatientById(appointment.patientId);
          const patientName = `${patientResponse.data.firstName} ${patientResponse.data.lastName}`;
          
          // Set the physicianId from the appointment data
          // The backend uses DoctorId but we use physicianId in the frontend
          // Both should contain the same user ID value
          const physicianId = String(appointment.doctorId || appointment.physicianId || '');
          
          setFormData({
            ...appointment,
            patientName,
            date: appointmentDate,
            time: timeDate,
            physicianId: physicianId,
            numberOfPersons: appointment.numberOfPersons || 1
          });
          
          // If we have physicians loaded, set the physician name
          if (physicians.length > 0) {
            const selectedPhysician = physicians.find(p => String(p.physicianId) === physicianId);
            if (selectedPhysician) {
              setFormData(prev => ({
                ...prev,
                physician: `${selectedPhysician.firstName} ${selectedPhysician.lastName}`
              }));
            }
          }
          
          setLoading(false);
        } catch (error) {
          console.error('Error fetching appointment data:', error);
          setError('Failed to load appointment data. Please try again later.');
          setLoading(false);
        }
      };
      
      fetchAppointmentData();
    }
  }, [id, isEditMode]);

  useEffect(() => {
    let timer;
    if (formData.isHouseCall && addressQuery && addressQuery.length > 2) {
      timer = setTimeout(async () => {
        setAddressLoading(true);
        try {
          const res = await apiClient.get('/location/geocode', { params: { query: addressQuery } });
          const items = (res.data && res.data.data) ? res.data.data : [];
          const options = items.map(item => ({
            label: item.label,
            lat: item.lat,
            lon: item.lon,
            address: item.address || {}
          }));
          setAddressOptions(options);
        } catch (e) {
          setAddressOptions([]);
        } finally {
          setAddressLoading(false);
        }
      }, 400);
    } else {
      setAddressOptions([]);
    }
    return () => { if (timer) clearTimeout(timer); };
  }, [formData.isHouseCall, addressQuery]);

  const queryAndSet = async (text) => {
    const q = (text || '').trim();
    if (!q) return;
    try {
      const res = await apiClient.get('/location/geocode', { params: { query: q } });
      const items = (res.data && res.data.data) ? res.data.data : [];
      const item = items[0] || null;
      if (item) {
        const a = item.address || {};
        const road = [a.house_number, a.road].filter(Boolean).join(' ');
        const cityValue = a.city || a.town || a.village || a.hamlet || a.suburb || '';
        const stateValue = a.state || a.region || a.state_district || '';
        const postalValue = a.postcode || '';
        setFormData(prev => ({
          ...prev,
          serviceAddress: road || item.label,
          serviceCity: cityValue,
          serviceState: 'Penang',
          servicePostalCode: postalValue
        }));
        setMapLat(parseFloat(item.lat));
        setMapLon(parseFloat(item.lon));
      }
    } catch {}
  };

  useEffect(() => {
    if (formData.isHouseCall && mapContainerRef.current && !leafletMap) {
      const initialLat = typeof mapLat === 'number' ? mapLat : 5.4141;
      const initialLon = typeof mapLon === 'number' ? mapLon : 100.3287;
      const map = L.map(mapContainerRef.current).setView([initialLat, initialLon], 13);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19
      }).addTo(map);
      const marker = L.marker([initialLat, initialLon], { draggable: true }).addTo(map);
      marker.on('dragend', () => {
        const pos = marker.getLatLng();
        setMapLat(pos.lat);
        setMapLon(pos.lng);
      });
      map.on('click', (e) => {
        const pos = e.latlng;
        marker.setLatLng(pos);
        setMapLat(pos.lat);
        setMapLon(pos.lng);
      });
      setLeafletMap(map);
      setLeafletMarker(marker);
    }
  }, [formData.isHouseCall, mapLat, mapLon, leafletMap]);

  useEffect(() => {
    if (leafletMap && leafletMarker) {
      const lat = typeof mapLat === 'number' ? mapLat : null;
      const lon = typeof mapLon === 'number' ? mapLon : null;
      if (lat !== null && lon !== null) {
        leafletMap.setView([lat, lon], leafletMap.getZoom());
        leafletMarker.setLatLng([lat, lon]);
      }
    }
  }, [mapLat, mapLon, leafletMap, leafletMarker]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (name === 'type') {
      setFormData(prev => ({
        ...prev,
        isHouseCall: value === 'House Call'
      }));
    }
  };
  
  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      date: date
    });
    
    // Check if the selected date is a holiday
    if (date) {
      checkIfHoliday(date);
    } else {
      setIsHoliday(false);
      setHolidayName('');
    }
  };
  
  // Function to check if a date is a holiday
  const checkIfHoliday = async (date) => {
    try {
      const response = await HolidayService.isHoliday(date);
      if (response && response.isHoliday) {
        setIsHoliday(true);
        setHolidayName(response.holidayName || 'Holiday');
      } else {
        setIsHoliday(false);
        setHolidayName('');
      }
    } catch (error) {
      console.error('Error checking if date is a holiday:', error);
      setIsHoliday(false);
      setHolidayName('');
    }
  };
  
  const handleTimeChange = (time) => {
    if (time) {
      const hours = time.getHours().toString().padStart(2, '0');
      const minutes = time.getMinutes().toString().padStart(2, '0');
      setFormData({
        ...formData,
        time: `${hours}:${minutes}`
      });
    } else {
      setFormData({
        ...formData,
        time: ''
      });
    }
  };
  
  const handlePatientChange = (e) => {
    const patientId = e.target.value;
    const selectedPatient = patients.find(patient => patient.patientId === patientId);
    
    setFormData({
      ...formData,
      patientId: patientId,
      patientName: selectedPatient ? `${selectedPatient.firstName} ${selectedPatient.lastName}` : ''
    });
  };
  
  const handleNewPatientToggle = (e) => {
    setFormData({
      ...formData,
      isNewPatient: e.target.checked,
      patientId: e.target.checked ? '' : formData.patientId
    });
  };
  
  const handleNewPatientChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      newPatient: {
        ...formData.newPatient,
        [name]: value
      }
    });
  };
  
  const handleNewPatientDateChange = (date) => {
    setFormData({
      ...formData,
      newPatient: {
        ...formData.newPatient,
        dateOfBirth: date
      }
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess(false);
    
    // Validate form data
    if (!formData.date) {
      setError('Please select an appointment date.');
      setSaving(false);
      return;
    }
    
    if (!formData.time) {
      setError('Please select a start time.');
      setSaving(false);
      return;
    }
    
    if (!formData.physicianId) {
      setError('Please select a physician.');
      setSaving(false);
      return;
    }
    
    if (formData.isNewPatient) {
      if (!formData.newPatient.nickname || !formData.newPatient.phoneNumber) {
        setError('Nickname and phone number are required for new patients.');
        setSaving(false);
        return;
      }
    } else if (!formData.patientId) {
      setError('Please select a patient.');
      setSaving(false);
      return;
    }
    
    if (formData.isRecurring) {
      if (!formData.occurrences || formData.occurrences < 2) {
        setError('Please specify at least 2 occurrences for recurring appointments.');
        setSaving(false);
        return;
      }
    }
    if (formData.isHouseCall) {
      if (!formData.serviceAddress || !formData.serviceCity || !formData.serviceState || !formData.servicePostalCode) {
        setError('Service address, city, state, and postal code are required for house call service.');
        setSaving(false);
        return;
      }
    }
    
    try {
      // Format date and time for API - use timezone-safe formatting
      const date = formData.date.getFullYear() + '-' + 
        String(formData.date.getMonth() + 1).padStart(2, '0') + '-' + 
        String(formData.date.getDate()).padStart(2, '0'); // YYYY-MM-DD
      
      // Handle time correctly whether it's a string or Date object
      let time;
      if (typeof formData.time === 'string') {
        // If time is already a string in HH:MM format, use it directly
        time = formData.time;
      } else if (formData.time instanceof Date) {
        // If time is a Date object, format it to HH:MM
        const hours = formData.time.getHours().toString().padStart(2, '0');
        const minutes = formData.time.getMinutes().toString().padStart(2, '0');
        time = `${hours}:${minutes}`;
      } else {
        setError('Invalid time format');
        setSaving(false);
        return;
      }
      

      
      // Prepare appointment data
      const appointmentData = {
        ...formData,
        date,
        time,
        // Convert physicianId to integer for DoctorId field
        physicianId: parseInt(formData.physicianId),
        // We'll let the backend calculate the endTime based on duration
        // so we don't need to send endTime
      };
      if (formData.isHouseCall) {
        appointmentData.serviceLatitude = typeof mapLat === 'number' ? mapLat : null;
        appointmentData.serviceLongitude = typeof mapLon === 'number' ? mapLon : null;
      }
      

      
      // Remove unnecessary fields
      delete appointmentData.patientName;
      
      // Prepare recurrence data if enabled
      if (formData.isRecurring) {
        // Format days of week for weekly recurrence
        if (formData.recurrenceType === 'Weekly' && Array.isArray(formData.daysOfWeek)) {
          appointmentData.daysOfWeek = formData.daysOfWeek.join(',');
        }
        
        // Format end date if provided - use timezone-safe formatting
        if (formData.endDate) {
          appointmentData.endDate = formData.endDate.getFullYear() + '-' + 
            String(formData.endDate.getMonth() + 1).padStart(2, '0') + '-' + 
            String(formData.endDate.getDate()).padStart(2, '0');
        }
      }
      
      if (isEditMode) {
        // Update existing appointment
        await AppointmentService.updateAppointment(parseInt(id), appointmentData);
      } else {
        // Create new appointment - use recurring endpoint if needed
        if (formData.isRecurring) {
          const response = await AppointmentService.createRecurringAppointment(appointmentData);
          
          // Check if there are any skipped holidays in the response
          if (response.data && response.data.skippedHolidays && response.data.skippedHolidays.length > 0) {
            setSkippedHolidays(response.data.skippedHolidays);
          }
        } else if (formData.isNewPatient) {
          // Format new patient data - use timezone-safe formatting
          const newPatientData = {
            ...formData.newPatient,
            dateOfBirth: formData.newPatient.dateOfBirth ? 
              formData.newPatient.dateOfBirth.getFullYear() + '-' + 
              String(formData.newPatient.dateOfBirth.getMonth() + 1).padStart(2, '0') + '-' + 
              String(formData.newPatient.dateOfBirth.getDate()).padStart(2, '0') : null
          };
          
          // Create appointment with new patient
          await AppointmentService.createAppointmentWithNewPatient(appointmentData, newPatientData);
        } else {
          await AppointmentService.createAppointment(appointmentData);
        }
      }
      
      setSuccess(true);
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate('/appointments');
      }, 1500);
    } catch (error) {
      console.error('Error saving appointment:', error);
      setError('Failed to save appointment. Please try again.');
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton
          sx={{ mr: 1 }}
          component={RouterLink}
          to="/appointments"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {isEditMode ? 'Edit Appointment' : 'New Appointment'}
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Appointment {isEditMode ? 'updated' : 'created'} successfully!
        </Alert>
      )}
      
      {isHoliday && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          The selected date ({formData.date?.toLocaleDateString()}) is a holiday: {holidayName}. You can still schedule the appointment, but please confirm with the patient.
        </Alert>
      )}
      
      {skippedHolidays.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2">The following dates in the recurring series were skipped because they fall on holidays:</Typography>
          <ul>
            {skippedHolidays.map((date, index) => (
              <li key={index}>{new Date(date).toLocaleDateString()}</li>
            ))}
          </ul>
        </Alert>
      )}
      
      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isNewPatient}
                      onChange={handleNewPatientToggle}
                      name="isNewPatient"
                      disabled={saving}
                    />
                  }
                  label="Create appointment for a new patient"
                />
              </Grid>
              
              {!formData.isNewPatient ? (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required disabled={loadingPatients || saving}>
                    <InputLabel id="patient-select-label">Patient</InputLabel>
                    <Select
                      labelId="patient-select-label"
                      id="patient-select"
                      name="patientId"
                      value={formData.patientId}
                      label="Patient"
                      onChange={handlePatientChange}
                    >
                      {loadingPatients ? (
                        <MenuItem value="">Loading patients...</MenuItem>
                      ) : (
                        patients.map((patient) => (
                          <MenuItem key={patient.patientId} value={patient.patientId}>
                            {patient.nickname ? `${patient.nickname} (${patient.firstName} ${patient.lastName})` : `${patient.firstName} ${patient.lastName}`}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              ) : (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                      New Patient Information
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nickname *"
                      name="nickname"
                      value={formData.newPatient.nickname}
                      onChange={handleNewPatientChange}
                      required
                      disabled={saving}
                      helperText="Required"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number *"
                      name="phoneNumber"
                      value={formData.newPatient.phoneNumber}
                      onChange={handleNewPatientChange}
                      required
                      disabled={saving}
                      helperText="Required - Format: +1 (555) 123-4567"
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                      The following information is optional and can be collected when the patient visits the clinic.
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="firstName"
                      value={formData.newPatient.firstName}
                      onChange={handleNewPatientChange}
                      disabled={saving}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="lastName"
                      value={formData.newPatient.lastName}
                      onChange={handleNewPatientChange}
                      disabled={saving}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Date of Birth"
                        value={formData.newPatient.dateOfBirth}
                        onChange={handleNewPatientDateChange}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            disabled: saving
                          }
                        }}
                        disabled={saving}
                      />
                    </LocalizationProvider>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth disabled={saving}>
                      <InputLabel id="gender-select-label">Gender</InputLabel>
                      <Select
                        labelId="gender-select-label"
                        id="gender-select"
                        name="gender"
                        value={formData.newPatient.gender}
                        label="Gender"
                        onChange={handleNewPatientChange}
                      >
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.newPatient.email}
                      onChange={handleNewPatientChange}
                      disabled={saving}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Address"
                      name="address"
                      value={formData.newPatient.address}
                      onChange={handleNewPatientChange}
                      disabled={saving}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Emergency Contact Name"
                      name="emergencyContactName"
                      value={formData.newPatient.emergencyContactName}
                      onChange={handleNewPatientChange}
                      disabled={saving}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Emergency Contact Phone"
                      name="emergencyContactPhone"
                      value={formData.newPatient.emergencyContactPhone}
                      onChange={handleNewPatientChange}
                      disabled={saving}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Insurance Provider"
                      name="insuranceProvider"
                      value={formData.newPatient.insuranceProvider}
                      onChange={handleNewPatientChange}
                      disabled={saving}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Insurance Policy Number"
                      name="insurancePolicyNumber"
                      value={formData.newPatient.insurancePolicyNumber}
                      onChange={handleNewPatientChange}
                      disabled={saving}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Medical Record Number"
                      name="medicalRecordNumber"
                      value={formData.newPatient.medicalRecordNumber}
                      onChange={handleNewPatientChange}
                      disabled={saving}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Official Document Number"
                      name="officialDocumentNumber"
                      value={formData.newPatient.officialDocumentNumber}
                      onChange={handleNewPatientChange}
                      disabled={saving}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="City"
                      name="city"
                      value={formData.newPatient.city}
                      onChange={handleNewPatientChange}
                      disabled={saving}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="State"
                      name="state"
                      value={formData.newPatient.state}
                      onChange={handleNewPatientChange}
                      disabled={saving}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Postal Code"
                      name="postalCode"
                      value={formData.newPatient.postalCode}
                      onChange={handleNewPatientChange}
                      disabled={saving}
                    />
                  </Grid>
                </>
              )}
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required disabled={saving}>
                  <InputLabel id="type-select-label">Appointment Type</InputLabel>
                  <Select
                    labelId="type-select-label"
                    id="type-select"
                    name="type"
                    value={formData.type}
                    label="Appointment Type"
                    onChange={handleInputChange}
                  >
                    <MenuItem value="Check-up">Check-up</MenuItem>
                    <MenuItem value="Consultation">Consultation</MenuItem>
                    <MenuItem value="Follow-up">Follow-up</MenuItem>
                    <MenuItem value="Treatment">Treatment</MenuItem>
                    <MenuItem value="Vaccination">Vaccination</MenuItem>
                    <MenuItem value="House Call">House Call</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isHouseCall}
                      onChange={(e) => setFormData({ ...formData, isHouseCall: e.target.checked })}
                      name="isHouseCall"
                      disabled={saving}
                    />
                  }
                  label="House Call Service"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={usePatientAddress}
                      onChange={async (e) => {
                        const checked = e.target.checked;
                        setUsePatientAddress(checked);
                        if (checked && formData.patientId) {
                          try {
                            const resp = await PatientService.getPatientById(formData.patientId);
                            const p = resp.data || {};
                            const addrString = [p.address, p.city, 'Penang', p.postalCode].filter(Boolean).join(' ');
                            setFormData(prev => ({
                              ...prev,
                              serviceAddress: p.address || '',
                              serviceCity: p.city || '',
                              serviceState: 'Penang',
                              servicePostalCode: p.postalCode || ''
                            }));
                            if (addrString) {
                              const res = await apiClient.get('/location/geocode', { params: { query: addrString } });
                              const items = (res.data && res.data.data) ? res.data.data : [];
                              const item = items[0] || null;
                              if (item) {
                                setMapLat(parseFloat(item.lat));
                                setMapLon(parseFloat(item.lon));
                              }
                            }
                          } catch {}
                        }
                      }}
                      name="usePatientAddress"
                      disabled={saving || !formData.patientId}
                    />
                  }
                  label="Use Patient Address"
                />
              </Grid>
              {formData.isHouseCall && (
                <>
                  <Grid item xs={12}>
                    <Autocomplete
                      freeSolo
                      filterOptions={(x) => x}
                      options={addressOptions}
                      inputValue={addressQuery}
                      value={addressSelected}
                      isOptionEqualToValue={(option, value) => option.label === (value?.label || value)}
                      getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
                      onInputChange={(e, value) => setAddressQuery(value || '')}
                      loading={addressLoading}
                      loadingText={addressLoading ? 'Searching…' : ''}
                      noOptionsText={addressQuery && addressQuery.length > 2 ? 'No results' : 'Type to search'}
                      onChange={(e, option) => {
                        if (!option) return;
                        setAddressSelected(option);
                        if (typeof option === 'string') {
                          queryAndSet(option);
                          return;
                        }
                        const a = option.address || {};
                        const road = [a.house_number, a.road].filter(Boolean).join(' ');
                        const cityValue = a.city || a.town || a.village || a.hamlet || a.suburb || '';
                        const stateValue = a.state || a.region || a.state_district || '';
                        const postalValue = a.postcode || '';
                        setFormData(prev => ({
                          ...prev,
                          serviceAddress: road || option.label,
                          serviceCity: cityValue,
                          serviceState: 'Penang',
                          servicePostalCode: postalValue
                        }));
                        setMapLat(parseFloat(option.lat));
                        setMapLon(parseFloat(option.lon));
                      }}
                      onBlur={(e) => {
                        const text = (e.target.value || '').trim();
                        if (text) queryAndSet(text);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Search Address" fullWidth />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Service Address"
                      name="serviceAddress"
                      value={formData.serviceAddress}
                      onChange={handleInputChange}
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      required
                      label="City"
                      name="serviceCity"
                      value={formData.serviceCity}
                      onChange={handleInputChange}
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      required
                      label="State"
                      name="serviceState"
                      value={formData.serviceState}
                      onChange={handleInputChange}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      required
                      label="Postal Code"
                      name="servicePostalCode"
                      value={formData.servicePostalCode}
                      onChange={handleInputChange}
                      disabled={saving}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ height: 300, border: '1px solid #ddd', borderRadius: 1 }} ref={mapContainerRef} />
                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                      {typeof mapLat === 'number' && typeof mapLon === 'number' ? `Pinned: ${mapLat.toFixed(6)}, ${mapLon.toFixed(6)}` : 'Click map to pin location'}
                    </Typography>
                  </Grid>
                  
                </>
              )}
              
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Appointment Date *"
                    value={formData.date}
                    onChange={handleDateChange}
                    renderInput={(params) => <TextField {...params} fullWidth required disabled={saving} />}
                    disabled={saving}
                  />
                </LocalizationProvider>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <TimePicker
                    label="Start Time *"
                    value={formData.time}
                    onChange={handleTimeChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        disabled: saving
                      }
                    }}
                    disabled={saving}
                  />
                </LocalizationProvider>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Duration (minutes) *"
                  name="duration"
                  type="number"
                  value={formData.duration}
                  onChange={handleInputChange}
                  required
                  disabled={saving}
                  InputProps={{ inputProps: { min: 5, max: 240 } }}
                  helperText="Appointment duration in minutes"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required disabled={loadingPhysicians || saving}>
                  <InputLabel id="physician-select-label">Practitioner</InputLabel>
                  <Select
                    labelId="physician-select-label"
                    id="physician-select"
                    name="physicianId"
                    value={formData.physicianId}
                    label="Practitioner"
                    onChange={(e) => {
                      const physicianId = String(e.target.value);
                      const selectedPhysician = physicians.find(p => String(p.physicianId) === physicianId);
                      const physicianName = selectedPhysician ? 
                        `${selectedPhysician.firstName} ${selectedPhysician.lastName}` : '';
                      

                      
                      setFormData({
                        ...formData,
                        physicianId: physicianId,
                        physician: physicianName
                      });
                    }}
                  >
                    {loadingPhysicians ? (
                      <MenuItem value="">Loading practitioners...</MenuItem>
                    ) : (
                      physicians.map((physician) => (
                        <MenuItem key={physician.physicianId} value={physician.physicianId}>
                          {`${physician.firstName} ${physician.lastName}`}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required disabled={saving}>
                  <InputLabel id="status-select-label">Status</InputLabel>
                  <Select
                    labelId="status-select-label"
                    id="status-select"
                    name="status"
                    value={formData.status}
                    label="Status"
                    onChange={handleInputChange}
                  >
                    <MenuItem value="Scheduled">Scheduled</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                    <MenuItem value="Cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Number of Persons"
                  name="numberOfPersons"
                  type="number"
                  value={formData.numberOfPersons}
                  onChange={handleInputChange}
                  disabled={saving}
                  InputProps={{ inputProps: { min: 1 } }}
                  helperText="Number of people attending this appointment"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  multiline
                  rows={4}
                  disabled={saving}
                />
              </Grid>
              
              {/* Recurring Appointment Options */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isRecurring}
                      onChange={(e) => setFormData({
                        ...formData,
                        isRecurring: e.target.checked
                      })}
                      name="isRecurring"
                      disabled={saving}
                    />
                  }
                  label="Make this a recurring appointment"
                />
              </Grid>
              
              {formData.isRecurring && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth disabled={saving}>
                      <InputLabel id="recurrence-type-label">Recurrence Pattern</InputLabel>
                      <Select
                        labelId="recurrence-type-label"
                        id="recurrence-type"
                        name="recurrenceType"
                        value={formData.recurrenceType}
                        label="Recurrence Pattern"
                        onChange={handleInputChange}
                      >
                        <MenuItem value="Daily">Daily</MenuItem>
                        <MenuItem value="Weekly">Weekly</MenuItem>
                        <MenuItem value="Monthly">Monthly</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Repeat every"
                      name="interval"
                      type="number"
                      value={formData.interval}
                      onChange={handleInputChange}
                      disabled={saving}
                      InputProps={{ inputProps: { min: 1, max: 12 } }}
                      helperText={`Repeat every ${formData.interval} ${formData.recurrenceType.toLowerCase()}`}
                    />
                  </Grid>
                  
                  {formData.recurrenceType === 'Weekly' && (
                    <Grid item xs={12}>
                      <FormControl component="fieldset" disabled={saving}>
                        <FormLabel component="legend">Repeat on</FormLabel>
                        <FormGroup row>
                          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, index) => (
                            <FormControlLabel
                              key={day}
                              control={
                                <Checkbox
                                  checked={formData.daysOfWeek.includes(index + 1)}
                                  onChange={(e) => {
                                    const dayValue = index + 1;
                                    let newDays = [...formData.daysOfWeek];
                                    
                                    if (e.target.checked) {
                                      newDays.push(dayValue);
                                    } else {
                                      newDays = newDays.filter(d => d !== dayValue);
                                    }
                                    
                                    setFormData({
                                      ...formData,
                                      daysOfWeek: newDays
                                    });
                                  }}
                                  name={`day-${index + 1}`}
                                />
                              }
                              label={day.substring(0, 3)}
                            />
                          ))}
                        </FormGroup>
                      </FormControl>
                    </Grid>
                  )}
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Number of occurrences"
                      name="occurrences"
                      type="number"
                      value={formData.occurrences}
                      onChange={handleInputChange}
                      disabled={saving}
                      InputProps={{ inputProps: { min: 2, max: 52 } }}
                      helperText="How many appointments to create in this series"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="End Date (Optional)"
                        value={formData.endDate}
                        onChange={(date) => setFormData({
                          ...formData,
                          endDate: date
                        })}
                        renderInput={(params) => <TextField {...params} fullWidth disabled={saving} />}
                        disabled={saving}
                      />
                    </LocalizationProvider>
                  </Grid>
                </>
              )}
              
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    sx={{ mr: 1 }}
                    component={RouterLink}
                    to="/appointments"
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Appointment'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AppointmentForm;